import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { callApi } from 'c-data-layer';
import ApiClient from 'c-data/apiClient';
import type { DashboardFilter } from './dashboardConfig';
import {
    DASHBOARD_ALL_FIELDS,
    DASHBOARD_FILTER_SOURCE_DEFAULT,
    toQueryFilters,
} from './dashboardConfig';
import type { DashboardReportRequest } from './useDashboardReport';
import { isCalcField } from './dashboardCalculations';
import { isRollingPeriod } from './dashboardRollingDates';

const SUGGESTION_LIMIT = 50;
const SEARCH_DEBOUNCE_MS = 300;

// A filter the API can run as it stands. A row still being filled in would fail the whole
// suggestion query, so it's left out, as is one on a calculated field, which only the browser
// can work out. A rolling date window is complete as soon as it is picked.
const isComplete = (filter: DashboardFilter): boolean => {
    if (isCalcField(filter.field)) return false;
    if (isRollingPeriod(filter.rolling)) return true;
    const { value } = filter;
    if (Array.isArray(value)) {
        return filter.operator === 'between'
            ? value.length === 2 && value.every(v => String(v).trim() !== '')
            : value.length > 0;
    }
    return value != null && String(value).trim() !== '';
};

// The default report's numeric columns are measures, not things anyone picks from a list, and
// listing their distinct values would scan the table for nothing.
const NUMERIC_DEFAULT_FIELDS = new Set(
    DASHBOARD_ALL_FIELDS.filter(f => f.type !== 'string').map(f => f.field),
);

export const offersValueSuggestions = (source: string | undefined, field: string): boolean =>
    source != null &&
    field !== '' &&
    !isCalcField(field) &&
    !(source === DASHBOARD_FILTER_SOURCE_DEFAULT && NUMERIC_DEFAULT_FIELDS.has(field));

// The field's most common values: the field as the only dimension, counted so the query groups
// (a dimension on its own is a plain projection, one row per record), most frequent first. The
// other filters around it narrow the list to values that can still match, and keep the scan
// to the period they cover. Those on the same field are left out, or picking one value would
// hide the rest.
export const filterValueSuggestionRequest = (
    field: string,
    search: string,
    filters: DashboardFilter[],
): Omit<DashboardReportRequest, 'datasourceId'> => {
    const term = search.trim();
    return {
        // Required, and the dynamic report only accepts bar/line/pie; neither query reads it.
        graphType: 'bar',
        dimensions: [{ field }],
        metrics: [{ field, aggregation: 'count' }],
        filters: toQueryFilters([
            ...filters.filter(f => f.field !== field && isComplete(f)),
            ...(term ? [{ field, operator: 'contains' as const, value: term }] : []),
        ]),
        sort: [{ field: `${field}_count`, direction: 'desc' }],
        limit: SUGGESTION_LIMIT,
    };
};

// Distinct values of one field on a dashboard source ('default' for the dynamic report,
// otherwise a datasource id), searched as the user types. Only fetches while `enabled`, i.e.
// while the picker is open, since every search is an Athena query.
const useFilterValueSuggestions = ({
    source,
    field,
    search,
    filters,
    enabled,
}: {
    source?: string;
    field: string;
    search: string;
    filters: DashboardFilter[];
    enabled: boolean;
}): { options: string[]; loading: boolean } => {
    const dispatch = useDispatch();
    const [results, setResults] = useState<Record<string, string[]>>({});
    const [loadingKey, setLoadingKey] = useState<string | null>(null);
    const requested = useRef<Set<string>>(new Set());

    const active = enabled && offersValueSuggestions(source, field);
    const request = active ? filterValueSuggestionRequest(field, search, filters) : null;
    const key = request ? JSON.stringify([source, request]) : null;

    useEffect(() => {
        if (!key || !request || source == null || requested.current.has(key)) return undefined;
        const timer = setTimeout(() => {
            requested.current.add(key);
            setLoadingKey(key);
            const { request: axiosRequest } =
                source === DASHBOARD_FILTER_SOURCE_DEFAULT
                    ? ApiClient.Reports.dashboard(request as any)
                    : ApiClient.Reports.datasourceQuery({
                          datasource: source,
                          ...(request as any),
                      });
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore - thunk dispatch typing, mirrors useDashboardReport
            dispatch(callApi(axiosRequest))
                .then(([err, , response]: [any, any, any]) => {
                    const rows: Record<string, unknown>[] = err ? [] : response?.data ?? [];
                    const values = rows
                        .map(row => row?.[field])
                        .filter(v => v != null && String(v).trim() !== '')
                        .map(v => String(v));
                    setResults(current => ({ ...current, [key]: [...new Set(values)] }));
                    setLoadingKey(current => (current === key ? null : current));
                    return undefined;
                })
                .catch(() => {
                    setResults(current => ({ ...current, [key]: [] }));
                    setLoadingKey(current => (current === key ? null : current));
                });
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
        // `request` is captured by `key`
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, source, dispatch]);

    return {
        options: key ? results[key] ?? [] : [],
        loading: key != null && (loadingKey === key || !(key in results)),
    };
};

export default useFilterValueSuggestions;
